import { useState } from 'react';
import { useAuth } from '@Context/CognitoAuthProvider';
import { accountsAPI } from '@Lib/axios/account';
import { useMemo } from 'react';
import TextModalInput from '@Common/TextModalInput';
import showToast from '@Common/toast/showToast';
import { CreatePromptModalProps } from '@Models/types';

const CreatePromptModal = ({
  isOpen,
  onClose,
  onSuccess,
  getNextVersion,
}: CreatePromptModalProps) => {
  const { user, logout } = useAuth();
  const account = useMemo(() => accountsAPI(logout), [logout]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation for required placeholders
  const validatePlaceholders = (content: string) => {
    const requiredPlaceholders = [
      '{user_story}',
      '{test_collection}',
      '{context}',
    ];
    const missingPlaceholders = requiredPlaceholders.filter(
      (placeholder) => !content.includes(placeholder)
    );
    return missingPlaceholders;
  };

  const handleCreate = async (content: string, nickname?: string) => {
    if (!content.trim()) {
      showToast.error('Please provide prompt content');
      return;
    }

    const missingPlaceholders = validatePlaceholders(content);
    if (missingPlaceholders.length > 0) {
      showToast.error(
        `Missing required placeholders: ${missingPlaceholders.join(', ')}`
      );
      return;
    }

    setIsSubmitting(true);
    try {
      await account.addPrompt(
        user?.profile?.email || '',
        content,
        getNextVersion(),
        {
          name: user?.profile?.name || '',
          email: user?.profile?.email || '',
        },
        true,
        nickname || undefined
      );

      await onSuccess?.();
      onClose();
      showToast.success('Prompt created successfully!');
    } catch (error) {
      console.error('Error creating prompt:', error);
      showToast.error('Failed to create prompt');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <TextModalInput
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleCreate}
      title="Create New Prompt"
      value=""
      nickname=""
      showNickname={true}
      loading={isSubmitting}
      submitBtnText="Create"
      cancelBtnText="Cancel"
      information={{
        title: 'Important Note:',
        description:
          'The prompt you enter will be appended to a pre-determined user story and format to ensure consistency in the generated test cases. Please make sure to include {user_story}, {test_collection}, and {context} placeholders.',
      }}
    />
  );
};

export default CreatePromptModal;
